import { useEffect, useMemo, useState } from "react";

import React from "react";
import Header from "@appComponents/Header/Header";
import classNames from "classnames";

import * as style from "./AccountPageStyle.module.scss";
import * as general from "@appStyles/general.module.scss";
import { tabs } from "./const";

import Chevron from "@images/chevron.svg";

import { Security } from "./sections/Security";
import { Profile } from "./sections/Profile";
import { Newsletter } from "./sections/Newsletter";
import { ApiManagement } from "./sections/ApiManagement";
import Loader from "@appComponents/Loader/Loader";
import { Promotions } from "./sections/Promotions";
import { AgreementsAndTerms } from "./sections/AgreementsAndTerms";
import { useMediaQuery } from "react-responsive";
import {
  AccountPageProps,
  AccountPageProvider,
  useAccountPageContext,
} from "./AcountPage.context";
import { DESKTOP_WIDTH } from "@bundles/consts";

const getTabFromUrl = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get("tab") || "profile";
};

const AccountPageContent = (props: AccountPageProps) => {
  const {
    state,
    isDataLoading,
    fetchProfileData: fetchProfileDatRoot,
    setIsDataLoading,
  } = useAccountPageContext();

  const isMobile = useMediaQuery({ maxWidth: DESKTOP_WIDTH });

  const [activeTab, setActiveTab] = useState(getTabFromUrl());

  const activeTabName = useMemo(
    () => tabs.find((tab) => tab.id === activeTab)?.label,
    [activeTab]
  );

  const fetchProfileData = () => {
    fetchProfileDatRoot();
  };

  const updateUrl = (tabId: string) => {
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set("tab", tabId);
    window.history.pushState({}, "", newUrl.toString());
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    updateUrl(tabId);
  };

  useEffect(() => {
    fetchProfileData();
  }, []);

  useEffect(() => {
    if (isMobile) {
      handleTabChange("");
    } else if (!isMobile && !activeTab) {
      handleTabChange("profile");
    }
  }, [isMobile]);

  useEffect(() => {
    const handlePopState = () => {
      setActiveTab(getTabFromUrl());
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  return (
    <>
      <Header padding={"0 20"}>
        <div className={classNames(general.profileHeaderWrapper)} />
      </Header>
      <div
        className={classNames(
          style.profileAccountContainer,
          style.profileAccountContainerNegativeMargin,
          "bg-transparent"
        )}
      >
        <div className="flex flex-col lg:flex-row">
          {(!isMobile || !activeTab) && (
            <div className="w-full bg-white lg:w-[313px] shadow-md p-8 lg:pt-[40px] h-fit">
              <h3 className="text-lg font-semibold mb-4">Account center</h3>
              <ul className="p-0 mt-[40px]">
                {tabs.map((tab) => (
                  <li
                    key={tab.id}
                    className={classNames(
                      "flex items-center gap-3 rounded-lg cursor-pointer transition-all duration-200 p-6 lg:p-3",
                      {
                        "hover:bg-gray-50": activeTab !== tab.id,
                        "bg-gray-100": activeTab === tab.id,
                      }
                    )}
                    onClick={() => handleTabChange(tab.id)}
                  >
                    {tab.icon}
                    <span>{tab.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!!activeTab && (
            <div className="flex-1 p-8 lg:py-[40px] lg:px-[60px] bg-white">
              {isMobile && (
                <a
                  className="inline-flex items-center gap-2 border-b border-[#323232] font-semibold mb-8"
                  onClick={() => handleTabChange("")}
                >
                  <Chevron className="rotate-180" />
                  Back
                </a>
              )}
              <div className="w-full">
                <h3
                  className={classNames(
                    "text-lg font-semibold border-b border-grey-lighter",
                    {
                      "pb-[40px]": !isMobile,
                      "pb-[20px]": isMobile,
                    }
                  )}
                >
                  {activeTabName}
                </h3>

                {isDataLoading ? (
                  <Loader />
                ) : (
                  <>
                    {activeTab === "profile" && (
                      <Profile
                        fetchProfileData={fetchProfileData}
                        setActiveTab={setActiveTab}
                      />
                    )}

                    {activeTab === "security" && <Security />}

                    {activeTab === "newsletter" && (
                      <Newsletter fetchProfileData={fetchProfileData} />
                    )}

                    {activeTab === "agreementAndTerms" && (
                      <AgreementsAndTerms fetchProfileData={fetchProfileData} />
                    )}

                    {activeTab === "api" && (
                      <ApiManagement investorId={props.investorId} />
                    )}

                    {activeTab === "promotions" && (
                      <Promotions
                        investorId={props.investorId}
                        profile={state.profileData?.profile}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export const AccountPage = (props) => (
  <AccountPageProvider {...props}>
    <AccountPageContent {...props} />
  </AccountPageProvider>
);

export default React.memo(AccountPage);
